import React, { useEffect, useState } from "react";
import "./index.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { setup2FA, enable2FA } from "../../../functions/auth";
import { useDispatch } from "react-redux";
import { userauthReducer } from "../../../components/toolkit/authReducer";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import Loading from "../../../modal/Loading";
import BackupCodesModal from "../../../components/BackupCodesModal";


const Setup = () => {
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState(""); // QR code URL directly from the backend
  const [secret, setSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState(1); // 1: Show QR, 2: Verify code
  const [otpAuthUrl, setOtpAuthUrl] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const location = useLocation();
  const { data } = location.state || {}
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const tokenC = data?.token?.accessToken;

  useEffect(() => {
    if (!tokenC) {
      navigate("/login");
      return;
    }

    // Initialize 2FA setup
    initialize2FA();
  }, [tokenC]);

  const initialize2FA = async () => {
    setLoading(true);
    try {
      const response = await setup2FA(tokenC);
      const { secret, qrCodeUrl, otpAuthUrl } = response.data.data;
      setSecret(secret);
      setQrCode(qrCodeUrl);
      setOtpAuthUrl(otpAuthUrl);
      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to initialize 2FA setup");
      setLoading(false);
    }
  };

  const handleEnable2FA = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await enable2FA(tokenC, verificationCode);
      toast.success("2FA enabled successfully!");
      if (data?.token?.accessToken) {
        cookies.set("token", data.token.accessToken, {
          expires: new Date(new Date().getTime() + 7 * 60 * 60 * 1000),
          maxAge: 7 * 60 * 60,
        });
      }
      if (data?.token?.refreshToken) {
        cookies.set("refreshToken", data.token.refreshToken, {
          expires: new Date(new Date().getTime() + 7 * 60 * 60 * 1000),
          maxAge: 7 * 60 * 60,
        });
      }
      if (data?.user) {
        cookies.set("userName", data.user.email);
        cookies.set("userId", data.user.id);
        cookies.set("name", data.user.name);
        localStorage.setItem("userData", JSON.stringify(data.user));
      }
      setLoading(false);

      // Show backup codes if provided
      if (response.data.data?.backupCodes) {
        setBackupCodes(response.data.data.backupCodes);
        setShowBackupCodesModal(true);
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to enable 2FA");
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast.success("Secret key copied to clipboard!");
  };

  const handleCloseBackupCodesModal = () => {
    setShowBackupCodesModal(false);
    setBackupCodes([]);
    navigate("/dashboard");
  };

  const renderSetupForm = () => (
    <div className="pcont">
      <div className="p-4">
        <div className="row">
          <div className="col-md-6 offset-md-3">
            <div className="text-center mb-4">
              <img
                alt=""
                src="/images/group.png"
                style={{
                  width: "6rem",
                  height: "5rem",
                  display: "flex",
                  margin: "auto",
                }}
              />
              <div className="textJ">Setup Two-Factor Authentication</div>
              <div
                className="textJ mt-2"
                style={{ fontSize: "14px", fontWeight: "400", color: "#666" }}
              >
                Secure your account with 2FA
              </div>
            </div>

            <div className="card p-4" style={{ backgroundColor: "white", borderRadius: "8px" }}>
              <h5 className="mb-3">Step 1: Scan QR Code</h5>
              <p className="text-muted mb-3">
                Use your Microsoft authenticator app to scan this QR code:
              </p>

              {qrCode && (
                <div className="text-center mb-3">
                  <img src={qrCode} alt="QR Code" style={{ maxWidth: "200px", height: "auto" }} />
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Or enter this secret key manually:</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={secret}
                    readOnly
                    style={{ fontFamily: "monospace" }}
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={copySecret}
                  >
                    Copy
                  </button>
                </div>
              </div>
              {otpAuthUrl && (
                <a href={otpAuthUrl} className="btn btn-outline-primary mb-3" target="_blank" rel="noopener noreferrer">
                  Open in Authenticator
                </a>
              )}
              <div className="d-grid">
                <button
                  className="btn btn-primary"
                  onClick={() => setStep(2)}
                  disabled={!qrCode}
                >
                  Next: Verify Code
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderVerificationForm = () => (
    <div className="pcont">
      <div className="p-4">
        <div className="row">
          <div className="col-md-4 offset-md-4">
            <div className="text-center mb-4">
              <img
                alt=""
                src="/images/group.png"
                style={{
                  width: "6rem",
                  height: "5rem",
                  display: "flex",
                  margin: "auto",
                }}
              />
              <div className="textJ">Verify Setup</div>
              <div
                className="textJ mt-2"
                style={{ fontSize: "14px", fontWeight: "400", color: "#666" }}
              >
                Enter the 6-digit code from your authenticator app
              </div>
            </div>

            <div className="card p-4" style={{ backgroundColor: "white", borderRadius: "8px" }}>
              <form onSubmit={handleEnable2FA}>
                <div className="mb-3">
                  <label className="form-label">Verification Code</label>
                  <input
                    type="text"
                    className="form-control text-center"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength="6"
                    style={{ fontSize: "1.5rem", letterSpacing: "0.5rem" }}
                    autoComplete="off"
                  />
                </div>

                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={verificationCode.length !== 6 || loading}
                  >
                    {loading ? "Enabling..." : "Enable 2FA"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container1">
      {loading && <Loading />}
      <div className="loginheader">
        <div className="d-flex gap-2 justify-content-center">
          <img
            alt=""
            src="/images/Union.png"
            style={{ width: "30px", height: "24px" }}
          />
          <h2 className="header">Pronto</h2>
        </div>
      </div>

      {step === 1 ? renderSetupForm() : renderVerificationForm()}

      <BackupCodesModal
        isOpen={showBackupCodesModal}
        onClose={handleCloseBackupCodesModal}
        backupCodes={backupCodes}
      />
    </div>
  );
};

export default Setup;
